/**
 * IncidentTimeline — vertical timeline with colour-coded status indicators per update.
 *
 * Status colours per update:
 *   detected     = grey
 *   investigating = yellow
 *   identified   = orange
 *   monitoring   = blue
 *   resolved     = green
 *
 * Shows a link to the postmortem at the bottom if one exists and is published.
 */

import type { IncidentUpdate, IncidentStatus, PostmortemReport } from '../../api/status.js';

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  IncidentStatus,
  { dot: string; connector: string; bg: string; text: string; label: string }
> = {
  detected:      { dot: 'bg-neutral-400',  connector: 'bg-neutral-200', bg: 'bg-neutral-50',  text: 'text-neutral-600',  label: 'Detected' },
  investigating: { dot: 'bg-amber-400',    connector: 'bg-amber-200',   bg: 'bg-amber-50',    text: 'text-amber-700',    label: 'Investigating' },
  identified:    { dot: 'bg-orange-500',   connector: 'bg-orange-200',  bg: 'bg-orange-50',   text: 'text-orange-700',   label: 'Identified' },
  monitoring:    { dot: 'bg-blue-500',     connector: 'bg-blue-200',    bg: 'bg-blue-50',     text: 'text-blue-700',     label: 'Monitoring' },
  resolved:      { dot: 'bg-emerald-500',  connector: 'bg-emerald-200', bg: 'bg-emerald-50',  text: 'text-emerald-700',  label: 'Resolved' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Very simple markdown → HTML for bold/italic/code inline formatting. */
function renderMessage(message: string): string {
  return message
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="bg-neutral-100 px-1 rounded text-sm font-mono">$1</code>');
}

// ── IncidentTimeline component ────────────────────────────────────────────────

interface IncidentTimelineProps {
  updates: IncidentUpdate[];
  postmortem?: PostmortemReport | null;
  incidentId?: string;
  className?: string;
}

export function IncidentTimeline({
  updates,
  postmortem,
  incidentId,
  className = '',
}: IncidentTimelineProps) {
  if (updates.length === 0) {
    return (
      <div className={`text-sm text-neutral-500 italic ${className}`}>
        No updates yet.
      </div>
    );
  }

  // Most recent first for display
  const sorted = [...updates].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  return (
    <div className={`relative ${className}`}>
      <ol className="space-y-0">
        {sorted.map((update, idx) => {
          const config = STATUS_CONFIG[update.status] ?? STATUS_CONFIG.detected;
          const isLast = idx === sorted.length - 1;

          return (
            <li key={update.id} className="relative flex gap-4 pb-6 last:pb-0">
              {/* Vertical connector line */}
              {!isLast && (
                <div
                  className={`absolute left-[7px] top-4 w-0.5 ${config.connector}`}
                  style={{ bottom: 0 }}
                  aria-hidden="true"
                />
              )}

              {/* Status dot */}
              <div className="flex-shrink-0 relative z-10">
                <div
                  className={`w-4 h-4 rounded-full border-2 border-white shadow-sm ${config.dot}`}
                  aria-label={config.label}
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-0.5">
                {/* Header row */}
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${config.text} ${config.bg}`}
                  >
                    {config.label}
                  </span>
                  <time
                    dateTime={update.created_at}
                    className="text-xs text-neutral-500"
                  >
                    {formatTimestamp(update.created_at)}
                  </time>
                </div>

                {/* Message (rendered markdown) */}
                <div
                  className="text-sm text-neutral-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderMessage(update.message) }}
                />

                {/* Component status changes */}
                {update.affected_components.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {update.affected_components.map((change, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-neutral-100 text-neutral-600"
                      >
                        {change.component_id} → {change.new_status}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Postmortem link */}
      {postmortem?.published_at && (
        <div className="mt-6 pt-4 border-t border-neutral-100">
          <a
            href={`/status/incidents/${incidentId}/postmortem`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            View Postmortem Report
          </a>
        </div>
      )}
    </div>
  );
}
